import { isArweaveGatewayUrl } from '../shared/arweave/publicArweaveGateway';
import { ARWEAVE_PUBLIC_GATEWAY_HOSTS, R2_PUBLIC_HOSTS } from '../lib/config';

export type NftStorageProvider = 'Arweave' | 'S3' | 'R2' | 'IPFS' | 'Unknown';
export type NftStorageType =
  | 'Permanent Storage'
  | 'Centralized Storage'
  | 'Decentralized Storage'
  | 'Unknown';

export interface NftStorageProviderInfo {
  name: string;
  type: NftStorageType;
  facilitator?: string;
}

function isArweaveBackedUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }

  return (
    isArweaveGatewayUrl(url, ARWEAVE_PUBLIC_GATEWAY_HOSTS) ||
    url.includes('irys')
  );
}

function isR2BackedUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }

  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return (
      R2_PUBLIC_HOSTS.includes(hostname) ||
      hostname === 'r2.dev' ||
      hostname.endsWith('.r2.dev') ||
      hostname === 'r2.cloudflarestorage.com' ||
      hostname.endsWith('.r2.cloudflarestorage.com')
    );
  } catch {
    return false;
  }
}

export function detectNftStorageProvider(
  nftMetadataUri: string | null | undefined,
  uploadProvider?: string | null,
  uploadFacilitator?: string | null,
): NftStorageProviderInfo {
  if (uploadProvider) {
    return {
      name: getProviderName(
        uploadProvider as NftStorageProvider,
        uploadFacilitator,
      ),
      type: getProviderType(uploadProvider as NftStorageProvider),
      facilitator: uploadFacilitator || undefined,
    };
  }

  if (!nftMetadataUri) {
    return { name: 'Not available', type: 'Unknown' };
  }

  if (isArweaveBackedUrl(nftMetadataUri)) {
    return { name: 'Arweave Network', type: 'Permanent Storage' };
  }

  if (isR2BackedUrl(nftMetadataUri)) {
    return { name: 'Cloudflare R2', type: 'Centralized Storage' };
  }

  if (nftMetadataUri.includes('amazonaws.com')) {
    return { name: 'AWS S3', type: 'Centralized Storage' };
  }

  if (nftMetadataUri.includes('ipfs')) {
    return { name: 'IPFS', type: 'Decentralized Storage' };
  }

  return { name: 'Unknown Provider', type: 'Unknown' };
}

function getProviderName(
  provider: NftStorageProvider,
  facilitator?: string | null,
): string {
  switch (provider) {
    case 'Arweave':
      return facilitator === 'ArDrive'
        ? 'Arweave Network via ArDrive'
        : 'Arweave Network';
    case 'S3':
      return 'AWS S3';
    case 'R2':
      return 'Cloudflare R2';
    case 'IPFS':
      return 'IPFS';
    default:
      return 'Unknown Provider';
  }
}

function getProviderType(provider: NftStorageProvider): NftStorageType {
  switch (provider) {
    case 'Arweave':
      return 'Permanent Storage';
    case 'S3':
      return 'Centralized Storage';
    case 'R2':
      return 'Centralized Storage';
    case 'IPFS':
      return 'Decentralized Storage';
    default:
      return 'Unknown';
  }
}

export function getNftStorageProviderLabel(
  nftMetadataUri: string | null | undefined,
  uploadProvider?: string | null,
): string {
  if (uploadProvider === 'Arweave' || isArweaveBackedUrl(nftMetadataUri)) {
    return 'Arweave';
  }

  if (uploadProvider === 'R2' || isR2BackedUrl(nftMetadataUri)) {
    return 'R2';
  }

  if (uploadProvider === 'S3' || nftMetadataUri?.includes('amazonaws.com')) {
    return 'S3';
  }

  if (uploadProvider === 'IPFS' || nftMetadataUri?.includes('ipfs')) {
    return 'IPFS';
  }

  return 'Provider';
}
